import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Delete, Get, Patch, Post } from "@repo/utils/api";
import Chainable from "@repo/utils/Chainable";
import UProxy from "@repo/utils/UProxy";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Bookable from "@repo/vygor-models/Bookable";

import { api } from "..";

import { Booking, useBookings } from "./bookings";
import { Customer, useCustomers } from "./customers";
import { Organization } from "./organizations";
import { Staff, useStaffs } from "./staffs";

export { Bookable };

// Top level bookables api is only for users, organizations will manage bookables through organization api
export type BookablesRest = Get<{}, { bookables: Bookable[] }> & {
  [id: Bookable.Id]: Get<
    { asAdmin?: boolean },
    {
      bookable: Bookable;
      bookableTemplate: Bookable.Template;
      bookableSupplement?: Bookable.AdminSupplement;
      bookableTemplateSupplement?: Bookable.Template.AdminSupplement;
      staff: Staff;
    }
  > & {
    bookings: Get<{}, { bookings: Booking[]; customers: Customer[] }>;
  };
};

export type BookableTemplatesRest = {
  [id: Bookable.Template.Id]: Get<
    { asAdmin?: boolean },
    {
      bookableTemplate: Bookable.Template;
      bookableTemplateSupplement?: Bookable.Template.AdminSupplement;
    }
  > & {
    bookables: Get<
      { asAdmin?: boolean },
      {
        bookables: Bookable[];
        staffs: Staff[];
        bookableSupplements?: Bookable.AdminSupplement[];
      }
    >;
  };
};

export namespace BookableTemplatesRest {
  export type Organization = Get<
    { asAdmin?: boolean },
    {
      bookableTemplates: Bookable.Template[];
      bookableTemplateSupplements?: Bookable.Template.AdminSupplement[];
    }
  > &
    Post<
      Bookable.Template.Params,
      {
        bookableTemplate: Bookable.Template;
        bookableTemplateSupplement: Bookable.Template.AdminSupplement;
      }
    > & {
      [id: string]: Patch<
        Partial<Bookable.Template.Params>,
        { bookableTemplate: Bookable.Template }
      > &
        Delete<{}>;
    };
}

const initialState = {
  byId: {} as Record<Bookable.Id, Bookable>,
  supplementById: {} as Record<Bookable.Id, Bookable.AdminSupplement>,
  bookingsById: {} as Record<Bookable.Id, Booking.Id[]>,
  templateById: {} as Record<Bookable.Template.Id, Bookable.Template>,
  templateSupplementById: {} as Record<
    Bookable.Template.Id,
    Bookable.Template.AdminSupplement
  >,
  listByTemplateId: {} as Record<
    Bookable.Template.Id,
    Bookable.Id[] & { asAdmin?: boolean }
  >,
  forOrganization: {
    templatesById: {} as Record<
      Organization.Id,
      Bookable.Template.Id[] & { asAdmin?: boolean }
    >,
  },
};

export const { Context, Provider, useBookables } = FeatureStore.init(
  "Bookables",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const [bookings, setBookings] = useBookings();
    const [, setStaffs] = useStaffs();
    const [, setCustomers] = useCustomers();

    const accessors = {
      byId: Object.assign(
        attachAction(
          (id: Bookable.Id | undefined): Bookable.Resolved | undefined =>
            id &&
            store.byId[id] &&
            UProxy.merge(store.templateById[store.byId[id]!.templateId]!, {
              ...store.byId[id]!,
              template: store.templateById[store.byId[id]!.templateId]!,
            }),
          (id) => id && actions.fetch(id),
          (id) => () => !id || store.byId[id],
        ),
        {
          admin: attachAction(
            (
              id: Bookable.Id | undefined,
            ):
              | (Bookable.Resolved & Partial<Bookable.AdminSupplement>)
              | undefined =>
              id &&
              store.byId[id] &&
              UProxy.merge(accessors.byId.peek(id)!, store.supplementById[id]),
            (id) => id && actions.fetch.asAdmin(id),
            (id) => () => !id || store.supplementById[id],
          ),
          bookings: attachAction(
            (id: Bookable.Id | undefined) =>
              id && store.bookingsById[id]?.map((id) => bookings.byId(id)!),
            (id) => id && actions.fetchBookings(id),
            (id) => (res) => !id || res,
          ),
        },
      ),
      templateById: Object.assign(
        attachAction(
          (id: Bookable.Template.Id | undefined) =>
            id && store.templateById[id],
          (id) => id && actions.fetchTemplate(id),
          (id) => () => !id || store.templateById[id],
        ),
        {
          admin: attachAction(
            (
              id: Bookable.Template.Id | undefined,
            ):
              | (Bookable.Template & Partial<Bookable.Template.AdminSupplement>)
              | undefined =>
              id &&
              store.templateById[id] &&
              UProxy.merge(
                store.templateById[id],
                store.templateSupplementById[id],
              ),
            (id) => id && actions.fetchTemplate.asAdmin(id),
            (id) => () => !id || store.templateSupplementById[id],
          ),
        },
      ),
      listByTemplateId: Object.assign(
        attachAction(
          (id: Bookable.Template.Id | undefined) =>
            id &&
            store.listByTemplateId[id]?.map((id) => accessors.byId.peek(id)!),
          (id) => id && actions.fetchForTemplate(id),
        ),
        {
          admin: attachAction(
            (id: Bookable.Template.Id | undefined) =>
              id &&
              store.listByTemplateId[id]?.asAdmin &&
              store.listByTemplateId[id].map(
                (id) => accessors.byId.admin.peek(id)!,
              ),
            (id) => id && actions.fetchForTemplate.asAdmin(id),
          ),
        },
      ),
      forOrganization: {
        templates: Object.assign(
          attachAction(
            (orgId: Organization.Id) =>
              store.forOrganization.templatesById[orgId]?.map(
                (id) => accessors.templateById.peek(id)!,
              ),
            (orgId) => actions.forOrganization.fetchTemplates(orgId),
          ),
          {
            admin: attachAction(
              (orgId: Organization.Id) =>
                store.forOrganization.templatesById[orgId]?.asAdmin &&
                store.forOrganization.templatesById[orgId].map(
                  (id) => accessors.templateById.admin.peek(id)!,
                ),
              (orgId) => actions.forOrganization.fetchTemplates.asAdmin(orgId),
            ),
          },
        ),
      },
    };

    const mutations = {
      one(bookable: Bookable) {
        setStore("byId", bookable.id, reconcile(bookable));
      },
      many(bookables: Bookable[]) {
        batch(() => {
          for (const bookable of bookables) {
            mutations.one(bookable);
          }
        });
      },
      oneSupplement(supplement: Bookable.AdminSupplement) {
        setStore("supplementById", supplement.id, reconcile(supplement));
      },
      manySupplements(bookables: Bookable.AdminSupplement[]) {
        batch(() => {
          for (const supplement of bookables) {
            mutations.oneSupplement(supplement);
          }
        });
      },
      bookings(id: Bookable.Id, bookings: Booking.Id[]) {
        setStore("bookingsById", id, bookings);
      },
      remove(id: Bookable.Id) {
        setStore(
          "byId",
          ({
            // eslint-disable-next-line no-unused-vars
            [id]: _,
            ...rest
          }) => rest,
        );
      },
      templates: {
        one(template: Bookable.Template) {
          setStore("templateById", template.id, reconcile(template));
        },
        many(templates: Bookable.Template[]) {
          batch(() => {
            for (const template of templates) {
              mutations.templates.one(template);
            }
          });
        },
        oneSupplement(supplement: Bookable.Template.AdminSupplement) {
          setStore(
            "templateSupplementById",
            supplement.id,
            reconcile(supplement),
          );
        },
        manySupplements(supplements: Bookable.Template.AdminSupplement[]) {
          batch(() => {
            for (const supplement of supplements) {
              mutations.templates.oneSupplement(supplement);
            }
          });
        },
      },
      forOrganization: {
        templates(
          orgId: Organization.Id,
          templates: Bookable.Template[] & { asAdmin?: boolean },
        ) {
          batch(() => {
            mutations.templates.many(templates);
            setStore(
              "forOrganization",
              "templatesById",
              orgId,
              Object.assign(
                templates.map(({ id }) => id),
                { asAdmin: templates.asAdmin },
              ),
            );
          });
        },
      },
    };

    const actions = wrapActions({
      ...mutations,
      fetch: Chainable.create(
        Chainable.wrapFactory(
          ({ asAdmin = false }) =>
            (id: Bookable.Id) =>
              api.bookables[id]!.GET({ asAdmin }).then(
                ({ bookable, bookableTemplate, bookableSupplement, staff }) => {
                  batch(() => {
                    mutations.one(bookable);
                    mutations.templates.one(bookableTemplate);
                    if (bookableSupplement)
                      mutations.oneSupplement(bookableSupplement);
                    if (staff) setStaffs.one(staff);
                  });
                },
              ),
        ),
        { asAdmin: { asAdmin: true } },
      ),
      fetchBookings(id: Bookable.Id) {
        return api.bookables[id]!.bookings.GET({}).then(
          ({ bookings, customers }) => {
            batch(() => {
              setCustomers.many(customers);
              setBookings.many(bookings);
              mutations.bookings(
                id,
                bookings.map((booking) => booking.id),
              );
            });
          },
        );
      },
      fetchTemplate: Chainable.create(
        Chainable.wrapFactory(
          ({ asAdmin = false }) =>
            (id: Bookable.Template.Id) =>
              api["bookable-templates"]
                [id]!.GET({ asAdmin })
                .then(({ bookableTemplate, bookableTemplateSupplement }) => {
                  batch(() => {
                    mutations.templates.one(bookableTemplate);
                    if (bookableTemplateSupplement)
                      mutations.templates.oneSupplement(
                        bookableTemplateSupplement,
                      );
                  });
                }),
        ),
        { asAdmin: { asAdmin: true } },
      ),
      fetchForTemplate: Chainable.create(
        Chainable.wrapFactory(
          ({ asAdmin = false }) =>
            (id: Bookable.Template.Id) =>
              api["bookable-templates"]
                [id]!.bookables.GET({ asAdmin })
                .then(({ bookables, staffs, bookableSupplements }) => {
                  batch(() => {
                    setStaffs.many(staffs);
                    mutations.many(bookables);
                    if (bookableSupplements)
                      mutations.manySupplements(bookableSupplements);
                    setStore(
                      "listByTemplateId",
                      id,
                      Object.assign(
                        bookables.map(({ id }) => id),
                        { asAdmin },
                      ),
                    );
                  });
                }),
        ),
        { asAdmin: { asAdmin: true } },
      ),
      forOrganization: {
        fetchTemplates: Chainable.create(
          Chainable.wrapFactory(
            ({ asAdmin = false }) =>
              (orgId: Organization.Id) => {
                return api.organizations[orgId]!["bookable-templates"].GET({
                  asAdmin,
                }).then(
                  ({ bookableTemplates, bookableTemplateSupplements }) => {
                    batch(() => {
                      mutations.forOrganization.templates(
                        orgId,
                        Object.assign(bookableTemplates, { asAdmin }),
                      );
                      if (bookableTemplateSupplements)
                        mutations.templates.manySupplements(
                          bookableTemplateSupplements,
                        );
                    });
                  },
                );
              },
          ),
          {
            asAdmin: { asAdmin: true },
          },
        ),
        createTemplate(
          orgId: Organization.Id,
          params: Bookable.Template.Params,
        ) {
          return api.organizations[orgId]!["bookable-templates"].POST(
            params,
          ).then(({ bookableTemplate, bookableTemplateSupplement }) => {
            batch(() => {
              mutations.templates.one(bookableTemplate);
              mutations.templates.oneSupplement(bookableTemplateSupplement);
              setStore(
                "forOrganization",
                "templatesById",
                orgId,
                (templates) => [...templates, bookableTemplate.id],
              );
            });
            return bookableTemplate;
          });
        },
        editTemplate(
          orgId: Organization.Id,
          templateId: Bookable.Template.Id,
          params: Bookable.Template.Params,
        ) {
          return api.organizations[orgId]!["bookable-templates"][
            templateId
          ]!.PATCH(params).then(({ bookableTemplate }) => {
            mutations.templates.one(bookableTemplate);
          });
        },
      },
    });

    return { accessors, actions };
  },
);
